import { useState, useRef, useCallback, useEffect } from 'react';

// ── AudioWorklet processor code (runs in audio thread) ──────
const WORKLET_PROCESSOR_CODE = `
class RecorderProcessor extends AudioWorkletProcessor {
  constructor() {
    super();
    this._active = true;
    this.port.onmessage = (e) => {
      if (e.data === 'stop') this._active = false;
    };
  }
  process(inputs) {
    if (!this._active) return false;
    const input = inputs[0];
    if (input && input.length > 0) {
      this.port.postMessage(new Float32Array(input[0]));
    }
    return true;
  }
}
registerProcessor('recorder-processor', RecorderProcessor);
`;

// ── WAV encoder (PCM Float32 → 16-bit WAV) ─────────────────
function encodeWav(samples: Float32Array, sampleRate: number): Blob {
  const numSamples = samples.length;
  const buffer = new ArrayBuffer(44 + numSamples * 2);
  const view = new DataView(buffer);

  const writeStr = (offset: number, str: string) => {
    for (let i = 0; i < str.length; i++) {
      view.setUint8(offset + i, str.charCodeAt(i));
    }
  };

  // RIFF header
  writeStr(0, 'RIFF');
  view.setUint32(4, 36 + numSamples * 2, true);
  writeStr(8, 'WAVE');

  // fmt chunk
  writeStr(12, 'fmt ');
  view.setUint32(16, 16, true);
  view.setUint16(20, 1, true);        // PCM format
  view.setUint16(22, 1, true);        // mono
  view.setUint32(24, sampleRate, true);
  view.setUint32(28, sampleRate * 2, true); // byte rate
  view.setUint16(32, 2, true);        // block align
  view.setUint16(34, 16, true);       // bits per sample

  // data chunk
  writeStr(36, 'data');
  view.setUint32(40, numSamples * 2, true);

  // Convert float32 [-1, 1] to int16
  for (let i = 0; i < numSamples; i++) {
    const s = Math.max(-1, Math.min(1, samples[i]));
    view.setInt16(44 + i * 2, s < 0 ? s * 0x8000 : s * 0x7FFF, true);
  }

  return new Blob([buffer], { type: 'audio/wav' });
}

export interface UseAudioRecorderOptions {
  audioConstraints?: MediaTrackConstraints;
}

interface UseAudioRecorderReturn {
  isRecording: boolean;
  audioBlob: Blob | null;
  audioUrl: string | null;
  startRecording: () => Promise<void>;
  stopRecording: () => void;
  clearRecording: () => void;
  error: string | null;
  analyserNode: AnalyserNode | null;
  getRecentMicPcm: (durationMs: number) => Float32Array | null;
  getSampleRate: () => number | null;
}

export function useAudioRecorder(options?: UseAudioRecorderOptions): UseAudioRecorderReturn {
  const [isRecording, setIsRecording] = useState(false);
  const [audioBlob, setAudioBlob] = useState<Blob | null>(null);
  const [audioUrl, setAudioUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [analyserNode, setAnalyserNode] = useState<AnalyserNode | null>(null);

  // Snapshot audioConstraints into a ref so callers can pass inline objects
  // without causing getStream/startRecording to re-create on every render
  const constraintsRef = useRef(options?.audioConstraints);
  constraintsRef.current = options?.audioConstraints;

  // Persistent refs — reused across recordings
  const streamRef = useRef<MediaStream | null>(null);
  const audioCtxRef = useRef<AudioContext | null>(null);
  const sourceRef = useRef<MediaStreamAudioSourceNode | null>(null);
  const sourceStreamRef = useRef<MediaStream | null>(null); // tracks which stream sourceRef was created from
  const analyserRef = useRef<AnalyserNode | null>(null);
  const workletRegisteredRef = useRef(false);

  // Per-recording refs
  const workletNodeRef = useRef<AudioWorkletNode | null>(null);
  const pcmChunksRef = useRef<Float32Array[]>([]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      // Stop worklet
      if (workletNodeRef.current) {
        workletNodeRef.current.port.postMessage('stop');
        workletNodeRef.current.disconnect();
        workletNodeRef.current = null;
      }
      // Stop stream tracks
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
        streamRef.current = null;
      }
      // Close AudioContext
      if (audioCtxRef.current && audioCtxRef.current.state !== 'closed') {
        audioCtxRef.current.close();
        audioCtxRef.current = null;
      }
    };
  }, []);

  const getStream = useCallback(async (): Promise<MediaStream> => {
    // Reuse existing stream if tracks are still active
    if (streamRef.current) {
      const tracks = streamRef.current.getAudioTracks();
      if (tracks.length > 0 && tracks[0].readyState === 'live') {
        return streamRef.current;
      }
      // Stream died, clean up
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }

    if (!navigator.mediaDevices?.getUserMedia) {
      const isLocalhost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
      if (!isLocalhost && window.location.protocol !== 'https:') {
        throw new Error('錄音功能需要 HTTPS 連線。請使用 Chrome 設定允許此網站使用麥克風，或透過 localhost 存取。');
      }
      throw new Error('您的瀏覽器不支援錄音功能。');
    }

    const constraints = constraintsRef.current ?? true;
    const stream = await navigator.mediaDevices.getUserMedia({ audio: constraints });
    streamRef.current = stream;
    return stream;
  }, []);

  const startRecording = useCallback(async () => {
    try {
      setError(null);

      const stream = await getStream();

      // Get or create AudioContext (reuse across recordings)
      let audioCtx = audioCtxRef.current;
      if (!audioCtx || audioCtx.state === 'closed') {
        audioCtx = new AudioContext();
        audioCtxRef.current = audioCtx;
        workletRegisteredRef.current = false;
        sourceRef.current = null;
        analyserRef.current = null;
      }

      // Resume if suspended (e.g. after browser autoplay policy)
      if (audioCtx.state === 'suspended') {
        await audioCtx.resume();
      }

      // Create source node — invalidate if the underlying stream changed
      if (!sourceRef.current || sourceStreamRef.current !== stream) {
        // Disconnect stale source before replacing
        if (sourceRef.current) {
          try { sourceRef.current.disconnect(); } catch { /* already disconnected */ }
        }
        sourceRef.current = audioCtx.createMediaStreamSource(stream);
        sourceStreamRef.current = stream;
        // Analyser was connected to old source, must recreate
        analyserRef.current = null;
      }

      // Create AnalyserNode (reuse)
      if (!analyserRef.current) {
        const analyser = audioCtx.createAnalyser();
        analyser.fftSize = 2048;
        sourceRef.current.connect(analyser);
        analyserRef.current = analyser;
        setAnalyserNode(analyser);
      }

      // Register worklet processor (once per AudioContext)
      if (!workletRegisteredRef.current) {
        const processorBlob = new Blob([WORKLET_PROCESSOR_CODE], { type: 'application/javascript' });
        const processorUrl = URL.createObjectURL(processorBlob);
        await audioCtx.audioWorklet.addModule(processorUrl);
        URL.revokeObjectURL(processorUrl);
        workletRegisteredRef.current = true;
      }

      // Clear previous PCM data
      pcmChunksRef.current = [];

      // Create new WorkletNode for this recording (lightweight)
      const workletNode = new AudioWorkletNode(audioCtx, 'recorder-processor');
      workletNode.port.onmessage = (e: MessageEvent<Float32Array>) => {
        pcmChunksRef.current.push(e.data);
      };
      sourceRef.current.connect(workletNode);
      workletNodeRef.current = workletNode;

      // Recording is now active — zero delay!
      setIsRecording(true);

      // Clear previous recording
      if (audioUrl) {
        URL.revokeObjectURL(audioUrl);
      }
      setAudioBlob(null);
      setAudioUrl(null);
    } catch (err) {
      console.error('Failed to start recording:', err);
      if (err instanceof Error) {
        if (err.name === 'NotAllowedError') {
          setError('Microphone permission denied. Please allow microphone access.');
        } else if (err.name === 'NotFoundError') {
          setError('No microphone found. Please connect a microphone.');
        } else {
          setError(err.message || 'Failed to start recording');
        }
      } else {
        setError('Failed to start recording');
      }
    }
  }, [audioUrl, getStream]);

  const stopRecording = useCallback(() => {
    if (!workletNodeRef.current) return;

    // Stop and disconnect worklet node
    workletNodeRef.current.port.postMessage('stop');
    workletNodeRef.current.disconnect();
    workletNodeRef.current = null;

    // Encode accumulated PCM to WAV
    const chunks = pcmChunksRef.current;
    if (chunks.length > 0) {
      const totalLength = chunks.reduce((sum, c) => sum + c.length, 0);
      const merged = new Float32Array(totalLength);
      let offset = 0;
      for (const chunk of chunks) {
        merged.set(chunk, offset);
        offset += chunk.length;
      }
      const sampleRate = audioCtxRef.current?.sampleRate || 48000;
      const wavBlob = encodeWav(merged, sampleRate);
      setAudioBlob(wavBlob);
      const url = URL.createObjectURL(wavBlob);
      setAudioUrl(url);
    }

    pcmChunksRef.current = [];
    setIsRecording(false);
    // Stream and AudioContext stay alive for reuse
  }, []);

  const getRecentMicPcm = useCallback((durationMs: number): Float32Array | null => {
    const ctx = audioCtxRef.current;
    if (!ctx) return null;
    const sr = ctx.sampleRate;
    const needSamples = Math.max(1, Math.floor((durationMs / 1000) * sr));
    const chunks = pcmChunksRef.current;
    if (chunks.length === 0) return null;

    let total = 0;
    for (let i = chunks.length - 1; i >= 0; i--) {
      total += chunks[i].length;
      if (total >= needSamples) break;
    }
    if (total < needSamples) return null;

    const result = new Float32Array(needSamples);
    let written = 0;
    let remaining = needSamples;
    for (let i = chunks.length - 1; i >= 0 && remaining > 0; i--) {
      const chunk = chunks[i];
      const take = Math.min(chunk.length, remaining);
      const srcStart = chunk.length - take;
      const dstStart = remaining - take;
      result.set(chunk.subarray(srcStart, srcStart + take), dstStart);
      remaining -= take;
      written += take;
    }
    if (written < needSamples) return null;
    return result;
  }, []);

  const getSampleRate = useCallback((): number | null => {
    return audioCtxRef.current?.sampleRate ?? null;
  }, []);

  const clearRecording = useCallback(() => {
    if (audioUrl) {
      URL.revokeObjectURL(audioUrl);
    }
    setAudioBlob(null);
    setAudioUrl(null);
    setError(null);
  }, [audioUrl]);

  return {
    isRecording,
    audioBlob,
    audioUrl,
    startRecording,
    stopRecording,
    clearRecording,
    error,
    analyserNode,
    getRecentMicPcm,
    getSampleRate,
  };
}
